import useEmblaCarousel from "embla-carousel-react";
import { useEffect, useMemo } from "react";

import { Link } from "react-router-dom";

import CheckIcon from "@/assets/icons/check.svg";
import ChevronLeft from "@/assets/icons/chevron-left.svg";
import ChevronRight from "@/assets/icons/chevron-right.svg";
import Computer from "@/assets/illustrations/computer.svg";
import Document from "@/assets/illustrations/document.svg";
import Headphoens from "@/assets/illustrations/headphones.svg";
import Megaphone from "@/assets/illustrations/megaphone.svg";
import { usePrevNextButtons } from "@/hooks/usePrevNextButtons";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { Article } from "@/types/Dashboard";
import { User } from "@/types/User";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import styles from "./GettingStarted.module.css";

const GettingStarted = (props: {
  readDashboardArticles?: number[];
  checklistArticles?: Article[];
  tipsArticles?: Article[];
  articlesError: Error | null;
}) => {
  const { data } = useUser();
  const language = (data?.data as User)?.language;

  const [emblaRef, emblaApi] = useEmblaCarousel();

  const {
    nextBtnDisabled,
    prevBtnDisabled,
    onNextButtonClick,
    onPrevButtonClick,
  } = usePrevNextButtons(emblaApi);

  const isAllChecklistArticlesRead = useMemo(() => {
    return (
      props.checklistArticles &&
      props.readDashboardArticles &&
      props.readDashboardArticles.length >= props.checklistArticles.length
    );
  }, [props.readDashboardArticles, props.checklistArticles]);

  useEffect(() => {
    if (isAllChecklistArticlesRead && emblaApi) {
      emblaApi.scrollTo(1, true);
    }
  }, [isAllChecklistArticlesRead, emblaApi]);

  return (
    <div className={styles["getting-started"]}>
      <div className={styles["getting-started__header"]}>
        <Typography
          variant="subtitle-1"
          className={styles["getting-started__header__title"]}
        >
          {L.DASHBOARD__GETTING_STARTED__TITLE}
        </Typography>

        <div className={styles["getting-started__header__controls"]}>
          <Tooltip
            content={L.DASHBOARD__GETTING_STARTED__CONTROLS__BROWSE_PREVIOUS}
          >
            <Button
              size="sm"
              variant="tertiary"
              icon={ChevronLeft}
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
            />
          </Tooltip>

          <Tooltip
            content={L.DASHBOARD__GETTING_STARTED__CONTROLS__BROWSE_NEXT}
          >
            <Button
              size="sm"
              variant="tertiary"
              icon={ChevronRight}
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
            />
          </Tooltip>
        </div>
      </div>

      <div ref={emblaRef} className={styles["getting-started__carousel"]}>
        <div className={styles["getting-started__carousel__inner"]}>
          <div
            data-testid="dashboard--getting-started--foundations"
            className={styles["getting-started__foundations"]}
          >
            <div className={styles["getting-started__foundations__inner"]}>
              <Typography
                variant="subtitle-1"
                className={styles["getting-started__foundations__inner__title"]}
              >
                {L.DASHBOARD__GETTING_STARTED__FOUNDATIONS__TITLE}
              </Typography>

              {props.checklistArticles ? (
                <div
                  className={
                    styles["getting-started__foundations__inner__list"]
                  }
                >
                  {props.checklistArticles.map((article, i) => {
                    const done = props.readDashboardArticles?.includes(
                      Number(article.id)
                    );

                    return (
                      <Link
                        key={article.id}
                        to={{
                          pathname: ROUTES.DASHBOARD,
                          search: new URLSearchParams({
                            article: article.id,
                          }).toString(),
                        }}
                        replace
                        data-done={done}
                        className={
                          styles[
                            "getting-started__foundations__inner__list__item"
                          ]
                        }
                      >
                        <div
                          className={
                            styles[
                              "getting-started__foundations__inner__list__item__badge"
                            ]
                          }
                        >
                          {done ? (
                            <CheckIcon
                              width="0.75rem"
                              strokeWidth={3}
                              stroke="white"
                            />
                          ) : (
                            <p
                              className={
                                styles[
                                  "getting-started__foundations__inner__list__item__badge__number"
                                ]
                              }
                            >
                              {i + 1}
                            </p>
                          )}
                        </div>
                        <Typography
                          variant="body-1"
                          className={
                            styles[
                              "getting-started__foundations__inner__list__item__text"
                            ]
                          }
                        >
                          {done ? (
                            <s>{article.translated_content[language].title}</s>
                          ) : (
                            article.translated_content[language].title
                          )}
                        </Typography>
                      </Link>
                    );
                  })}
                </div>
              ) : props.articlesError ? (
                <Typography>{L.GENERIC_ERROR}</Typography>
              ) : (
                <div
                  className={
                    styles[
                      "getting-started__foundations__inner__spinner-container"
                    ]
                  }
                >
                  <Spinner />
                </div>
              )}
            </div>

            <Document width="12rem" />
          </div>

          <div
            data-testid="dashboard--getting-started--advanced"
            className={styles["getting-started__advanced__list"]}
          >
            {props.tipsArticles?.map((article, i) => {
              const Illustration = [Computer, Headphoens, Megaphone][i];

              const colors = [
                {
                  header: "var(--pink-50)",
                  footer: "var(--pink-200)",
                },
                {
                  header: "var(--blue-100)",
                  footer: "var(--blue-300)",
                },
                {
                  header: "var(--primary-100)",
                  footer: "var(--primary-300)",
                },
              ][i];

              return (
                <Link
                  key={article.id}
                  className={styles["getting-started__advanced__list__item"]}
                  to={{
                    pathname: ROUTES.DASHBOARD,
                    search: new URLSearchParams({
                      article: article.id,
                    }).toString(),
                  }}
                >
                  <div
                    className={
                      styles["getting-started__advanced__list__item__header"]
                    }
                    style={{ backgroundColor: colors.header }}
                  >
                    {Illustration && (
                      <Illustration
                        className={
                          styles[
                            "getting-started__advanced__list__item__header__icon"
                          ]
                        }
                      />
                    )}
                  </div>

                  <div
                    style={{ backgroundColor: colors.footer }}
                    className={
                      styles["getting-started__advanced__list__item__footer"]
                    }
                  >
                    <Typography
                      variant="body-1"
                      className={
                        styles[
                          "getting-started__advanced__list__item__footer__title"
                        ]
                      }
                    >
                      {article.translated_content[language].title}
                    </Typography>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStarted;
